
///
/// EarthBrowser
///
/// Created for Passkey Authentication
/// Copyright (c) 2025 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { Icons } from "@/components/ui/icons"
import { toast } from "@/components/ui/use-toast"
import {
	useGetPasskeyRegistrationOptionsMutation,
	useRegisterPasskeyMutation,
	useGetPasskeyCredentialsQuery,
	useDeletePasskeyCredentialMutation,
	useRenamePasskeyCredentialMutation,
} from "@/redux/userApi"
import { isWebAuthnSupported, registerPasskey } from "@/utils/passkey"
import type { PasskeyCredential } from "@shared-types/passkey"
import { extractErrorMessage } from "@/utils/error-handler"
import { Check, Edit2, KeyRoundIcon, Loader2, Trash2, X } from "lucide-react"
import { SectionHeader } from "./SectionHeader"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export function PasskeysSection() {
	const [ isRegistering, setIsRegistering ] = useState(false)
	const [ editingCredentialId, setEditingCredentialId ] = useState<string | null>(null)
	const [ editingName, setEditingName ] = useState("")
	const [ deleteCredentialId, setDeleteCredentialId ] = useState<string | null>(null)
	const isWebAuthnAvailable = isWebAuthnSupported()

	const [ getRegistrationOptions ] = useGetPasskeyRegistrationOptionsMutation()
	const [ registerPasskeyMutation ] = useRegisterPasskeyMutation()
	const { data: credentialsData, isLoading: isLoadingCredentials } = useGetPasskeyCredentialsQuery()
	const [ deleteCredential ] = useDeletePasskeyCredentialMutation()
	const [ renameCredential ] = useRenamePasskeyCredentialMutation()

	const credentials = credentialsData?.credentials || []

	async function handleAddPasskey() {
		if (!isWebAuthnAvailable) {
			toast({
				title: "Passkeys Not Supported",
				variant: "destructive",
				description: "Your browser does not support passkeys. Please use a browser with WebAuthn support.",
			})
			return
		}

		setIsRegistering(true)
		try {
			// Get registration options from server
			const optionsResult = await getRegistrationOptions(undefined)

			if ("error" in optionsResult && optionsResult.error) {
				const errorMessage = extractErrorMessage(optionsResult.error, "Failed to get registration options")
				throw new Error(errorMessage)
			}

			if (!optionsResult.data) {
				throw new Error("Failed to get registration options from server")
			}

			// Perform WebAuthn registration
			const credential = await registerPasskey(optionsResult.data)

			// Send credential to server
			const result = await registerPasskeyMutation({
				credential,
				name: `Passkey ${new Date().toLocaleDateString()}`,
			})

			if ("error" in result && result.error) {
				const errorMessage = extractErrorMessage(result.error, "Failed to register passkey")
				throw new Error(errorMessage)
			}

			if (result.data?.success) {
				toast({
					title: "Passkey Added Successfully",
					description: "Your new passkey has been registered and can now be used to sign in.",
				})
			} else {
				throw new Error("Passkey registration did not complete successfully")
			}
		} catch (error) {
			console.error("Add passkey error:", error)

			// Provide user-friendly error messages
			let errorMessage = "An unexpected error occurred"

			if (error instanceof Error) {
				errorMessage = error.message

				// Handle specific browser WebAuthn errors
				if (error.name === "NotAllowedError") {
					errorMessage = "Passkey creation was cancelled or permission was denied. Please try again."
				} else if (error.name === "InvalidStateError") {
					errorMessage = "This passkey already exists on this device. Please use a different device or authenticator."
				} else if (error.name === "NotSupportedError") {
					errorMessage = "Your authenticator doesn't support this type of passkey. Please try a different authenticator."
				} else if (error.name === "SecurityError") {
					errorMessage = "A security error occurred. Please ensure you're on a secure connection."
				} else if (error.name === "AbortError") {
					errorMessage = "Passkey creation timed out. Please try again."
				}
			}

			toast({
				title: "Failed to Add Passkey",
				variant: "destructive",
				description: errorMessage,
			})
		} finally {
			setIsRegistering(false)
		}
	}

	async function handleDeletePasskey(credentialId: string) {
		try {
			const result = await deleteCredential({ credentialId })
			if (result.data?.success) {
				toast({
					title: "Passkey Deleted",
					description: "Your passkey has been removed",
				})
			} else {
				throw new Error("Failed to delete passkey")
			}
		} catch (error) {
			console.error("Delete passkey error:", error)
			toast({
				title: "Failed to Delete Passkey",
				variant: "destructive",
				description: "Unable to remove passkey",
			})
		}
		setDeleteCredentialId(null)
	}

	async function handleRenamePasskey(credentialId: string, name: string) {
		try {
			const result = await renameCredential({ credentialId, name })
			if (result.data?.success) {
				toast({
					title: "Passkey Renamed",
					description: "Your passkey name has been updated",
				})
				setEditingCredentialId(null)
			} else {
				throw new Error("Failed to rename passkey")
			}
		} catch (error) {
			console.error("Rename passkey error:", error)
			toast({
				title: "Failed to Rename Passkey",
				variant: "destructive",
				description: "Unable to update passkey name",
			})
		}
	}

	function startEditing(credentialId: string, currentName: string) {
		setEditingCredentialId(credentialId)
		setEditingName(currentName)
	}

	function cancelEditing() {
		setEditingCredentialId(null)
		setEditingName("")
	}

	if (!isWebAuthnAvailable) {
		return (
			<div className="space-y-4">
				<div>
					<h3 className="text-lg font-medium">Passkeys</h3>
					<p className="text-sm text-muted-foreground">
						Your browser does not support passkeys
					</p>
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-3">
			<SectionHeader
				icon={KeyRoundIcon}
				title="Passkeys"
				description={credentials.length > 0
					? `${credentials.length} passkey${credentials.length > 1 ? 's' : ''} configured for secure, password-free authentication`
					: 'Add a passkey for secure, password-free authentication'}
				color="cyan"
			/>

			<div className="space-y-3">
				{isLoadingCredentials ? (
					<div className="flex items-center justify-center py-4">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : credentials.length === 0 ? (
					<div className="rounded-lg border border-dashed border-border p-4 flex items-center justify-center">
						<p className="text-sm text-muted-foreground italic">
							No passkeys yet
						</p>
					</div>
				) : (
					<div className="space-y-2">
						{credentials.map((cred: PasskeyCredential) => (
							<div
								key={cred.credentialId}
								className="flex items-center justify-between rounded-lg border border-border bg-background p-3 transition-colors hover:bg-muted/50"
							>
								<div className="flex items-center space-x-3">
									<div className="p-1.5 rounded-lg bg-muted">
										<Icons.key className="h-4 w-4 text-muted-foreground" />
									</div>
									<div>
										{editingCredentialId === cred.credentialId ? (
											<div className="flex items-center space-x-2">
												<Input
													value={editingName}
													onChange={(e) => setEditingName(e.target.value)}
													className="h-8 w-64 rounded-lg"
													autoFocus
												/>
												<Button
													size="sm"
													variant="ghost"
													onClick={() =>
														handleRenamePasskey(cred.credentialId, editingName)
													}
												>
													<Check className="h-4 w-4" />
												</Button>
												<Button
													size="sm"
													variant="ghost"
													onClick={cancelEditing}
												>
													<X className="h-4 w-4" />
												</Button>
											</div>
										) : (
											<div>
												<p className="text-sm font-medium">{cred.name}</p>
												<p className="text-xs text-muted-foreground">
													Last used:{" "}
													{new Date(cred.lastUsedAt).toLocaleDateString()}
												</p>
											</div>
										)}
									</div>
								</div>
								{editingCredentialId !== cred.credentialId && (
									<div className="flex items-center space-x-2">
										<Button
											size="sm"
											variant="ghost"
											onClick={() => startEditing(cred.credentialId, cred.name)}
										>
											<Edit2 className="h-4 w-4" />
										</Button>
										<Button
											size="sm"
											variant="ghost"
											onClick={() => setDeleteCredentialId(cred.credentialId)}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								)}
							</div>
						))}
					</div>
				)}
				<Button
					onClick={handleAddPasskey}
					disabled={isRegistering}
					variant="outline"
					className="w-full rounded-lg"
				>
					{isRegistering ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Registering...
						</>
					) : (
						<>
							<Icons.key className="mr-2 h-4 w-4" />
							Add Passkey
						</>
					)}
				</Button>
			</div>

			<AlertDialog
				open={deleteCredentialId !== null}
				onOpenChange={(open) => !open && setDeleteCredentialId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete Passkey</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete this passkey? This action cannot be
							undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								deleteCredentialId && handleDeletePasskey(deleteCredentialId)
							}
						>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
